// AgentBrainApp.tsx — the Memory/Brain app (Board App, Erez 2026-07-24).
//
// Agent-centric. LEFT sidebar = every agent (avatar circle + name, Jarvis first).
// Click one → the agent's detail: a top-bar switches the main document between
// SESSION SUMMARY and PROJECT SUMMARY (+ COMPANY BRAIN for Jarvis), and the
// SESSION LOGS run down the right side. Each agent's minimum = summary + project
// + logs. Principles live at the project level (inside the project summary).
//
// Reuses existing prod endpoints (so it works on localhost via the /api proxy):
//   /api/agents                                   → the roster
//   /api/kb/kb-doc/agents/<name>/session-summary  → session summary (blocks)
//   /api/projects  +  /api/projects/<slug>        → project summary (markdown)
//   /api/memories?agent=<name>&type=session_log   → session logs
//   /api/kb/kb-doc/company/learnings              → the company brain (Jarvis)
//
// React-Flow trap: non-draggable node = pointer-events:none. Every interactive /
// scroll element carries pointerEvents:"auto" + nowheel/nodrag/nopan, and
// clickables stop pointerdown from starting a canvas pan.

import { useEffect, useMemo, useState } from "react";
import { Loader2, ScrollText, FileText, FolderKanban, BookOpen } from "lucide-react";
import { useApi } from "@/lib/api";
import { BlockRenderer } from "../blueprint/BlockRenderer";
import { architectureConfig } from "../blueprint/ArchitectureBlocks";

const AV = "https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/avatars";

const T = {
  ink: "#191D1B", ink2: "#55605A", faint: "#98A099",
  paper: "#FAF9F6", card: "#FFFFFF", bone: "#F3F0EA",
  line: "#EAE7E0", hair: "#E2DED6",
  accent: "#2E6B52", accentSoft: "#EAF2ED", accentLine: "#CDE1D6",
  amber: "#B07A1E",
};
const FONT = "'Inter Tight', Inter, system-ui, -apple-system, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

type Agent = { name: string; display_name: string; color: string | null; voice_kokoro?: string | null };
type Project = { slug: string; name: string; owner_agent: string | null; current_focus: string | null; board_order: number | null };
type Memory = { id: string; title: string | null; body: string; created_at: string };
type Blocks = { blocks: unknown[] };

const cap = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);

function timeAgo(iso: string): string {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.round(h / 24)}d`;
}

// ── avatar with graceful fallback to a colored initial ────────────────────────
function Avatar({ agent, size }: { agent: Agent; size: number }) {
  const [err, setErr] = useState(false);
  const bg = agent.color || T.accent;
  if (err) {
    return (
      <div style={{ width: size, height: size, borderRadius: 999, background: bg, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: size * 0.42, flexShrink: 0 }}>
        {cap(agent.display_name || agent.name).slice(0, 1)}
      </div>
    );
  }
  return (
    <img
      src={`${AV}/${agent.name.toLowerCase()}.jpg`}
      alt=""
      onError={() => setErr(true)}
      style={{ width: size, height: size, borderRadius: 999, objectFit: "cover", flexShrink: 0, background: T.bone, border: `1px solid ${T.hair}` }}
    />
  );
}

// ── tiny markdown renderer for project-summary text ───────────────────────────
function MarkdownLite({ text }: { text: string }) {
  const lines = (text || "").split("\n");
  const out: React.ReactNode[] = [];
  let list: string[] = [];
  const flush = (k: number) => {
    if (!list.length) return;
    out.push(
      <ul key={`ul${k}`} style={{ margin: "8px 0 14px", paddingLeft: 22, display: "flex", flexDirection: "column", gap: 6 }}>
        {list.map((li, i) => <li key={i} style={{ fontSize: 15, lineHeight: 1.6, color: T.ink2 }}>{inline(li)}</li>)}
      </ul>,
    );
    list = [];
  };
  const inline = (s: string): React.ReactNode => {
    const parts = s.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => (p.startsWith("**") && p.endsWith("**"))
      ? <strong key={i} style={{ color: T.ink, fontWeight: 700 }}>{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>);
  };
  lines.forEach((raw, k) => {
    const l = raw.trimEnd();
    if (/^#{1,6}\s/.test(l)) {
      flush(k);
      const level = (l.match(/^#+/) || ["#"])[0].length;
      const txt = l.replace(/^#+\s/, "");
      out.push(<div key={k} style={{ fontSize: level <= 2 ? 18 : 15.5, fontWeight: 800, color: T.ink, margin: "18px 0 8px", letterSpacing: "-0.01em" }}>{txt}</div>);
    } else if (/^\s*[-*]\s/.test(l)) {
      list.push(l.replace(/^\s*[-*]\s/, ""));
    } else if (l.trim() === "") {
      flush(k);
    } else {
      flush(k);
      out.push(<p key={k} style={{ fontSize: 15, lineHeight: 1.7, color: T.ink2, margin: "0 0 10px" }}>{inline(l)}</p>);
    }
  });
  flush(lines.length);
  return <div>{out}</div>;
}

type Tab = "summary" | "project" | "brain";

// ── the detail (document + logs) for one agent ────────────────────────────────
function AgentDetail({ agent, project }: { agent: Agent; project: Project | null }) {
  const api = useApi();
  const [tab, setTab] = useState<Tab>("summary");
  const [summary, setSummary] = useState<Blocks | null | "loading" | "none">("loading");
  const [projectDoc, setProjectDoc] = useState<string | null | "loading" | "none">("loading");
  // Set when the project has a real KB page; takes precedence over projectDoc.
  const [projectBlocks, setProjectBlocks] = useState<Blocks | null>(null);
  const [brain, setBrain] = useState<Blocks | null | "loading" | "none">("loading");
  const [logs, setLogs] = useState<Memory[] | null>(null);
  const isJarvis = agent.name.toLowerCase() === "jarvis";

  useEffect(() => { setTab("summary"); }, [agent.name]);

  // session summary
  useEffect(() => {
    let dead = false; setSummary("loading");
    (async () => {
      try {
        const r = await api(`/api/kb/kb-doc/agents/${agent.name.toLowerCase()}/session-summary`);
        if (!r.ok) { if (!dead) setSummary("none"); return; }
        const d = await r.json();
        const blocks = d?.content?.blocks ?? d?.content ?? null;
        if (!dead) setSummary(blocks ? { blocks } : "none");
      } catch { if (!dead) setSummary("none"); }
    })();
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent.name]);

  // Project document. Since 2026-07-26 the real project doc is a KB PAGE at
  // kb-doc/projects/<slug>, on the skill://project template — so it renders with
  // the full component library like every other document, instead of the
  // hand-rolled MarkdownLite that only understood bold and bullets.
  //
  // We try the KB page FIRST and fall back to the legacy `projects.summary` text
  // column, because the conversion is still in flight — a project without a page
  // yet must keep showing its old summary rather than going blank.
  useEffect(() => {
    let dead = false; setProjectDoc("loading"); setProjectBlocks(null);
    if (!project) { setProjectDoc("none"); return; }
    (async () => {
      try {
        const kb = await api(`/api/kb/kb-doc/projects/${project.slug}`);
        if (kb.ok) {
          const d = await kb.json();
          const blocks = d?.content?.blocks ?? d?.blocks ?? null;
          if (blocks?.length) { if (!dead) { setProjectBlocks({ blocks }); setProjectDoc(null); } return; }
        }
      } catch { /* fall through to the legacy column */ }
      try {
        const r = await api(`/api/projects/${project.slug}`);
        if (!r.ok) { if (!dead) setProjectDoc("none"); return; }
        const d = await r.json();
        const s = d?.summary ?? d?.project?.summary ?? null;
        if (!dead) setProjectDoc(s || "none");
      } catch { if (!dead) setProjectDoc("none"); }
    })();
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent.name, project?.slug]);

  // company brain (jarvis)
  useEffect(() => {
    let dead = false;
    if (!isJarvis) { setBrain("none"); return; }
    setBrain("loading");
    (async () => {
      try {
        const r = await api(`/api/kb/kb-doc/company/learnings`);
        if (!r.ok) { if (!dead) setBrain("none"); return; }
        const d = await r.json();
        const blocks = d?.content?.blocks ?? d?.content ?? null;
        if (!dead) setBrain(blocks ? { blocks } : "none");
      } catch { if (!dead) setBrain("none"); }
    })();
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent.name]);

  // session logs
  useEffect(() => {
    let dead = false; setLogs(null);
    (async () => {
      try {
        const r = await api(`/api/memories?agent=${agent.name.toLowerCase()}&type=session_log`);
        if (!r.ok) { if (!dead) setLogs([]); return; }
        const d = await r.json();
        if (!dead) setLogs(Array.isArray(d) ? d : (d.rows ?? []));
      } catch { if (!dead) setLogs([]); }
    })();
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent.name]);

  const TABS: { id: Tab; label: string; icon: typeof FileText; show: boolean }[] = [
    { id: "summary", label: "Session Summary", icon: FileText, show: true },
    { id: "project", label: "Project Summary", icon: FolderKanban, show: true },
    { id: "brain", label: "Company Brain", icon: BookOpen, show: isJarvis },
  ];

  const loading = (
    <div style={{ padding: 30, color: T.faint, fontSize: 15, display: "inline-flex", alignItems: "center", gap: 8 }}>
      <Loader2 style={{ width: 15, height: 15, animation: "spin 1s linear infinite" }} /> Loading…
    </div>
  );
  const empty = (what: string) => <div style={{ padding: 30, color: T.faint, fontSize: 15, fontStyle: "italic" }}>{what}</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {/* agent header */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "24px 30px 18px", borderBottom: `1px solid ${T.line}` }}>
        <Avatar agent={agent} size={56} />
        <div>
          <div style={{ fontSize: 24, fontWeight: 800, color: T.ink, letterSpacing: "-0.02em" }}>{cap(agent.display_name || agent.name)}</div>
          <div style={{ fontSize: 13, color: T.faint, fontFamily: MONO }}>{project ? project.name : "no project · chief of staff"}</div>
        </div>
      </div>

      {/* body: document (left) + logs (right) */}
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        {/* document */}
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", borderRight: `1px solid ${T.line}` }}>
          {/* top-bar tabs */}
          <div style={{ display: "flex", gap: 6, padding: "14px 24px", borderBottom: `1px solid ${T.line}` }}>
            {TABS.filter((t) => t.show).map((t) => {
              const on = t.id === tab;
              return (
                <button key={t.id} type="button" onClick={() => setTab(t.id)} onPointerDown={(e) => e.stopPropagation()} className="nodrag nopan"
                  style={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 600, cursor: "pointer", pointerEvents: "auto", display: "inline-flex", alignItems: "center", gap: 7, color: on ? "#fff" : T.ink2, background: on ? T.accent : T.card, border: `1px solid ${on ? T.accent : T.hair}`, borderRadius: 999, padding: "7px 15px" }}>
                  <t.icon style={{ width: 15, height: 15 }} /> {t.label}
                </button>
              );
            })}
          </div>
          {/* document content */}
          <div className="nowheel nodrag nopan" style={{ flex: 1, overflow: "auto", padding: "6px 30px 34px", pointerEvents: "auto" }}>
            {tab === "summary" && (summary === "loading" ? loading : summary === "none" || !summary ? empty("No session summary yet.") : <BlockRenderer config={architectureConfig} blocks={summary.blocks as never} />)}
            {tab === "project" && (
              projectBlocks
                ? <BlockRenderer config={architectureConfig} blocks={projectBlocks.blocks as never} />
                : projectDoc === "loading" ? loading
                : projectDoc === "none" || !projectDoc ? empty("No project — this agent's memory is personal; principles live at the project level.")
                : <div style={{ paddingTop: 22 }}><MarkdownLite text={projectDoc as string} /></div>
            )}
            {tab === "brain" && (brain === "loading" ? loading : brain === "none" || !brain ? empty("—") : <BlockRenderer config={architectureConfig} blocks={brain.blocks as never} />)}
          </div>
        </div>

        {/* session logs */}
        <div style={{ width: 420, flexShrink: 0, display: "flex", flexDirection: "column", background: T.paper }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "16px 20px 12px", borderBottom: `1px solid ${T.line}` }}>
            <ScrollText style={{ width: 16, height: 16, color: T.accent }} />
            <span style={{ fontSize: 13.5, fontWeight: 800, color: T.ink, textTransform: "uppercase", letterSpacing: "0.06em" }}>Session Logs</span>
            <span style={{ marginLeft: "auto", fontFamily: MONO, fontSize: 12, color: T.faint }}>{logs ? logs.length : "…"}</span>
          </div>
          <div className="nowheel nodrag nopan" style={{ flex: 1, overflow: "auto", padding: "12px 16px 20px", pointerEvents: "auto" }}>
            {logs === null ? loading : logs.length === 0 ? empty("No logs.") : logs.map((m) => (
              <div key={m.id} style={{ background: T.card, border: `1px solid ${T.hair}`, borderRadius: 11, padding: "12px 14px", marginBottom: 9 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 5 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: T.ink, lineHeight: 1.3 }}>{m.title || "log"}</span>
                  <span style={{ fontFamily: MONO, fontSize: 10.5, color: T.faint, flexShrink: 0 }}>{timeAgo(m.created_at)}</span>
                </div>
                <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                  {(m.body || "").replace(/^[-*#>\s]+/gm, "").replace(/\n+/g, " ").trim()}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── the app ───────────────────────────────────────────────────────────────────
export function AgentBrainApp() {
  const api = useApi();
  const [agents, setAgents] = useState<Agent[] | null>(null);
  const [projectByAgent, setProjectByAgent] = useState<Record<string, Project>>({});
  const [selected, setSelected] = useState<string>("jarvis");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const [ra, rp] = await Promise.all([api("/api/agents"), api("/api/projects")]);
        if (!ra.ok) throw new Error(`agents ${ra.status}`);
        const abody = await ra.json();
        const list: Agent[] = (Array.isArray(abody) ? abody : (abody.rows ?? [])).map((a: Agent) => ({ name: a.name, display_name: a.display_name, color: a.color }));
        const pmap: Record<string, Project> = {};
        if (rp.ok) {
          const pbody = await rp.json();
          (Array.isArray(pbody) ? pbody : (pbody.rows ?? [])).forEach((p: Project) => { if (p.owner_agent) pmap[p.owner_agent.toLowerCase()] = p; });
        }
        if (!dead) { setAgents(list); setProjectByAgent(pmap); }
      } catch (e) { if (!dead) setError(e instanceof Error ? e.message : "failed"); }
    })();
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Jarvis first, then agents that own a project (the real team), then the rest.
  const ordered = useMemo(() => {
    if (!agents) return [];
    const rank = (a: Agent) => {
      if (a.name.toLowerCase() === "jarvis") return -1;
      return projectByAgent[a.name.toLowerCase()] ? (projectByAgent[a.name.toLowerCase()].board_order ?? 500) : 999;
    };
    return [...agents].sort((x, y) => rank(x) - rank(y) || (x.display_name || x.name).localeCompare(y.display_name || y.name));
  }, [agents, projectByAgent]);

  const current = ordered.find((a) => a.name.toLowerCase() === selected.toLowerCase()) ?? ordered[0] ?? null;

  const H = 1180;

  return (
    <div className="nopan nodrag" style={{ display: "flex", width: 300 + 1400, height: H, fontFamily: FONT, pointerEvents: "auto", background: T.card, border: `1px solid ${T.hair}`, borderRadius: 20, overflow: "hidden", boxShadow: "0 8px 24px rgba(25,29,27,0.05)" }}>
      {/* sidebar */}
      <div className="nowheel nodrag nopan" style={{ width: 300, flexShrink: 0, overflow: "auto", background: T.paper, borderRight: `1px solid ${T.line}`, padding: "20px 14px", pointerEvents: "auto" }}>
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.12em", color: T.faint, textTransform: "uppercase", fontFamily: MONO, padding: "0 8px 12px" }}>Agents</div>
        {error ? <div style={{ padding: 12, color: "#C0492F", fontSize: 13 }}>{error}</div>
          : !agents ? <div style={{ padding: 12, color: T.faint, fontSize: 14, display: "inline-flex", gap: 8, alignItems: "center" }}><Loader2 style={{ width: 14, height: 14, animation: "spin 1s linear infinite" }} /> …</div>
          : ordered.map((a) => {
              const on = a.name.toLowerCase() === (current?.name.toLowerCase() ?? "");
              const proj = projectByAgent[a.name.toLowerCase()];
              return (
                <button key={a.name} type="button" onClick={() => setSelected(a.name)} onPointerDown={(e) => e.stopPropagation()} className="nodrag nopan"
                  style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left", cursor: "pointer", pointerEvents: "auto", fontFamily: FONT, padding: "9px 10px", marginBottom: 3, borderRadius: 12, border: `1px solid ${on ? T.accentLine : "transparent"}`, background: on ? T.accentSoft : "transparent" }}>
                  <Avatar agent={a} size={40} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: on ? 800 : 600, color: T.ink, lineHeight: 1.2 }}>{cap(a.display_name || a.name)}</div>
                    <div style={{ fontSize: 11.5, color: T.faint, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{proj ? proj.name : (a.name.toLowerCase() === "jarvis" ? "Chief of staff" : "—")}</div>
                  </div>
                </button>
              );
            })}
      </div>

      {/* detail */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {current ? <AgentDetail agent={current} project={projectByAgent[current.name.toLowerCase()] ?? null} /> : null}
      </div>
    </div>
  );
}

export default AgentBrainApp;
